import {
  FormControl,
  FormHelperText,
  OutlinedTextFieldProps,
  TextField,
} from "@mui/material";
import dayjs from "dayjs";
import { Controller, useFormContext, useController } from "react-hook-form";

interface IHLTextField extends Partial<OutlinedTextFieldProps> {
  name: string;
}

/**
 * Custom text field, linked with React Hook Form context
 * @param name linked with value obj
 * @param type input type
 */
const HLTextField = ({ name, type, ...rest }: IHLTextField) => {
  const { control } = useFormContext();
  const { fieldState } = useController({ name, control });

  return (
    <FormControl fullWidth>
      <Controller
        name={name}
        control={control}
        defaultValue=""
        render={({ field, fieldState }) => {
          const { onChange, onBlur, value } = field;

          const handleBlur = () => {
            if (type !== "number" && value != null) {
              onChange(String(value).trim());
            }
            onBlur?.();
          };

          const handleChange = (event: any) => {
            let {
              target: { value: newValue }
            } = event;
            if (type === "number" && newValue) newValue = +newValue;
            onChange(newValue);
          };

          // Compute display value for date inputs without mutating field.value.
          // Mutating field.value during render is a React anti-pattern and
          // dayjs("") / dayjs(null).format(...) returns "Invalid Date", which
          // would both warn in the console and corrupt the controlled input.
          const displayValue =
            type === "date" && value
              ? dayjs(value).isValid()
                ? dayjs(value).format("YYYY-MM-DD")
                : ""
              : value ?? "";

          return (
            <TextField
              value={displayValue}
              onChange={handleChange}
              onBlur={handleBlur}
              type={type}
              {...rest}
              variant="outlined"
              error={fieldState.error !== undefined}
              InputLabelProps={{ shrink: true }}
              // Allow comma decimal separators
              lang="de"
            />
          );
        }}
      />
      {fieldState.error && (
        <FormHelperText error>{fieldState.error.message}</FormHelperText>
      )}
    </FormControl>
  );
};

export default HLTextField;
